import type { conditionFields, conditionOps } from "@veritrellis/shared";

type ConditionField = (typeof conditionFields)[number];
type ConditionOp = (typeof conditionOps)[number];

export type FieldType = "number" | "string" | "enum";

export type FieldDef = {
  key: ConditionField;
  label: string;
  description: string;
  type: FieldType;
  operators: ConditionOp[];
  enumValues?: readonly string[];
};

const NUMBER_OPS: ConditionOp[] = ["gt", "gte", "lt", "lte", "eq", "neq"];
const ENUM_OPS: ConditionOp[] = ["eq", "neq", "in", "not_in"];
const STRING_OPS: ConditionOp[] = ["eq", "neq"];

export const fieldCatalog: readonly FieldDef[] = [
  {
    key: "amount",
    label: "Amount",
    description: "Numeric amount of the action (refund, credit, or price change)",
    type: "number",
    operators: NUMBER_OPS
  },
  {
    key: "risk_level",
    label: "Risk level",
    description: "Risk level explicitly set by the caller on the request",
    type: "enum",
    operators: ENUM_OPS,
    enumValues: ["low", "medium", "high"]
  },
  {
    key: "risk_class",
    label: "Risk class",
    description: "Risk class derived from amount, currency, and side-effect type",
    type: "enum",
    operators: ENUM_OPS,
    enumValues: ["low", "medium", "high"]
  },
  {
    key: "customer_tier",
    label: "Customer tier",
    description: "Tier of the customer account the action targets",
    type: "enum",
    operators: ENUM_OPS,
    enumValues: ["free", "pro", "enterprise", "smb"]
  },
  {
    key: "environment",
    label: "Environment",
    description: "Sandbox or production environment of the request",
    type: "enum",
    operators: ["eq", "neq"],
    enumValues: ["sandbox", "production"]
  },
  {
    key: "action_subtype",
    label: "Action subtype",
    description: "Optional subtype tag set by the caller (e.g. channel for messages)",
    type: "string",
    operators: STRING_OPS
  },
  {
    key: "origin_system",
    label: "Origin system",
    description: "The agent platform or integration that submitted the request",
    type: "enum",
    operators: ENUM_OPS,
    enumValues: [
      "openai_mcp",
      "anthropic_mcp",
      "copilot_connector",
      "hubspot_wf",
      "intercom_fin",
      "zendesk_app",
      "sdk_direct",
      "admin_ui",
      "unknown"
    ]
  },
  {
    key: "chain_amount_sum",
    label: "Chain total",
    description: "Running sum of amounts across all requests sharing the same chain ID",
    type: "number",
    operators: NUMBER_OPS
  }
] as const;

export const fieldByKey = new Map<ConditionField, FieldDef>(fieldCatalog.map((f) => [f.key, f]));

export const getFieldDef = (key: ConditionField): FieldDef =>
  fieldByKey.get(key) ?? fieldCatalog[0]!;

export const opLabel: Record<ConditionOp, string> = {
  eq: "=",
  neq: "≠",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  in: "in",
  not_in: "not in"
};

/** Readable operator labels for inline condition rows */
export const opLabelInline = (op: ConditionOp, fieldType: FieldType): string => {
  if (fieldType === "number") {
    const numeric: Record<ConditionOp, string> = {
      eq: "equals",
      neq: "does not equal",
      gt: "is more than",
      gte: "is at least",
      lt: "is less than",
      lte: "is at most",
      in: "is in",
      not_in: "is not in"
    };
    return numeric[op];
  }
  const textual: Record<ConditionOp, string> = {
    eq: "is",
    neq: "is not",
    gt: "is greater than",
    gte: "is at least",
    lt: "is less than",
    lte: "is at most",
    in: "is any of",
    not_in: "is none of"
  };
  return textual[op];
};

export type FieldGroup = {
  label: string;
  fields: readonly ConditionField[];
};

export const fieldGroups: readonly FieldGroup[] = [
  {
    label: "Financial",
    fields: ["amount", "chain_amount_sum"]
  },
  {
    label: "Risk",
    fields: ["risk_level", "risk_class"]
  },
  {
    label: "Customer & environment",
    fields: ["customer_tier", "environment"]
  },
  {
    label: "Request context",
    fields: ["action_subtype", "origin_system"]
  }
] as const;
